import { PageRoutes } from '@/constants/PageRoutes'
import { ElTableColumn } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { OrderDirection } from 'network'
import { useBaseInfoStore, useDoubleContractAssetsAccountStore, useDoubleContractKlineStore, useExchangeInfoStore, useLanguageStore, useProjectTheme, useQuoteStore, useResponsive } from 'store'
import { ArrowRight, ControlledAmount, CustomButton, EmptyData, FullScreenMinHeight, HocElTable, ListCell, PlusIcon, Pnl, Tag, UiUtils } from 'ui-components'
import { ContractCalculation } from 'utils'
import '../styles/order-list-layout.scss'
import '../styles/position-item-layout.scss'

type OpenPositionProps = {
  fromAssetAccount?: boolean
}

export const OpenPosition = defineComponent<OpenPositionProps>({
  props: {
    fromAssetAccount: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useI18n()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const quoteStore = useQuoteStore()
    const responsive = useResponsive()
    const languageStore = useLanguageStore()
    const baseInfoStore = useBaseInfoStore()
    const exchangeStore = useExchangeInfoStore()
    const klineStore = useDoubleContractKlineStore()
    const contractAssetsAccountStore = useDoubleContractAssetsAccountStore()
    const router = useRouter()

    const getMarkPrice = (row: ApiResponse.DoubleContractPosition) => {
      const symbol = quoteStore.getSymbolBySymbolIdWithContractList(row.symbolId)
      return symbol?.markPrice
    }

    const getPositionValue = (row: ApiResponse.DoubleContractPosition) => {
      const markPrice = getMarkPrice(row)
      return row.positionAmt.multiplyStr(markPrice || 1).formatCommonMoney()
    }

    const getPnl = (row: ApiResponse.DoubleContractPosition) => {
      const pnl = ContractCalculation.getPnl({
        openPrice: row.holdPrice,
        closePrice: getMarkPrice(row),
        qty: row.qty,
        contractValue: row.contractValue,
        dir: row.direction.toString(),
      })
      return pnl.cutDecimal(2)
    }

    const getPnlRate = (row: ApiResponse.DoubleContractPosition) => {
      const pnl = getPnl(row)
      const iMargin = row.qty.multiplyStr(row.contractValue).multiplyStr(row.holdPrice).divideStr(row.leverage)
      return pnl.divideStr(iMargin).multiplyStr(100).ceil(2)
    }

    const handleClosePosition = (row: ApiResponse.DoubleContractPosition) => {
      UiUtils.showClosePosition({
        position: row,
        onClose: () => {},
      })
    }

    const handleUpdateMargin = (row: ApiResponse.DoubleContractPosition) => {
      UiUtils.showUpdateMargin({
        position: row,
      })
    }

    const handleStopProfitStopLoss = (row: ApiResponse.DoubleContractPosition) => {
      UiUtils.showStopProfitStopLoss({
        position: row,
      })
    }

    const handlePositionDetail = (row: ApiResponse.DoubleContractPosition) => {
      UiUtils.showPositionDetail({
        position: row,
      })
    }

    const positions = computed(() => {
      return contractAssetsAccountStore.positions.filter((item) => {
        if (contractAssetsAccountStore.hideOtherOrder) {
          return item.symbolId === klineStore.currentSymbolId
        }
        return true
      })
    })

    const changeSymbol = (row: ApiResponse.DoubleContractPosition) => {
      if (row.symbolId === klineStore.currentSymbolId) return
      klineStore.updateSymbolId(row.symbolId)
      router.push({
        path: PageRoutes.ContractTrade,
        query: {
          symbol: row.symbolId,
        },
      })
    }

    return () => {
      if (responsive.isMobile) {
        if (positions.value.length === 0) {
          return (
            <div>
              <EmptyData />
            </div>
          )
        }
        return positions.value.map((item) => (
          <div class="position-item-layout position-item divider" onClick={() => changeSymbol(item)}>
            <div class="symbol-coin-info !mb-[8px]">
              <img width={32} height={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(item.symbolId)} alt="coin icon" />
              <div class="coin-info">
                <div class="coin-name">
                  {exchangeStore.getContractSymbolName(item.symbolId)} {t('永续')}
                </div>
                <div class="flex info-tags gap-4">
                  <Tag color={item.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{item.direction === OrderDirection.Buy ? t('做多') : t('做空')}</Tag>
                  <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                    {item.marginType === 1 ? t('逐仓') : t('全仓')} {item.leverage}X
                  </Tag>
                </div>
              </div>
            </div>
            <div onClick={() => handlePositionDetail(item)}>
              <div class="flex justify-between !mb-[10px]">
                <div>
                  <Pnl value={getPnl(item)} />
                  <span class="text-[var(--colors-text-secondary)] !text-[14px]">{t('未实现盈亏')}</span>
                </div>
                <div>
                  <Pnl value={getPnlRate(item)} isRate />
                  <span class="text-[var(--colors-text-secondary)] !text-[14px]">{t('收益率')}</span>
                </div>
              </div>

              {/* 持仓数量 */}
              <ListCell
                title={t('持仓数量')}
                description={
                  <ControlledAmount>
                    {item.positionAmt.formatNumberWithCommas()} {exchangeStore.getFromSymbolNameBySymbolId(item.symbolId)}
                    {' / '}
                    {getPositionValue(item)} {exchangeStore.getToSymbolNameBySymbolId(item.symbolId)}
                  </ControlledAmount>
                }
              />

              {/* 开仓均价 */}
              <ListCell title={t('开仓均价')} description={<ControlledAmount>{item.holdPrice.formatNumberWithCommas()}</ControlledAmount>} />

              {/* 标记价格 */}
              <ListCell title={t('标记价格')} description={<span>{getMarkPrice(item)?.formatNumberWithCommas() || '--'}</span>} />

              {/* 保证金 */}
              <ListCell
                title={t('保证金') + '(USDT)'}
                description={
                  <div class="flex flex-row items-center !gap-[4px] ">
                    <ControlledAmount>{item.margin.formatNumberWithCommas()}</ControlledAmount>

                    {item.marginType === 1 && (
                      <span class="cursor-pointer" onClick={() => handleUpdateMargin(item)}>
                        <PlusIcon></PlusIcon>
                      </span>
                    )}
                  </div>
                }
              />

              {/* 强平价 */}
              <ListCell title={t('强平价')} description={<ControlledAmount>{item.liquidationPrice.formatNumberWithCommas()}</ControlledAmount>} />

              {/* 操作 */}
              <div class="flex justify-between items-center w-full">
                <CustomButton class="!bg-[var(--colors-bg-tag-base)] !border-none w-full" onClick={() => handleClosePosition(item)}>
                  {t('平仓')}
                </CustomButton>
                <CustomButton class="!bg-[var(--colors-bg-tag-base)] !border-none w-full" onClick={() => handleStopProfitStopLoss(item)}>
                  {t('止盈止损')}
                </CustomButton>
              </div>
            </div>
          </div>
        ))
      }

      return (
        <div class={'contract-trade-order-list-layout has-cursor'}>
          <HocElTable data={positions.value} onRow-click={(row: ApiResponse.DoubleContractPosition) => changeSymbol(row)}>
            {{
              default: () => (
                <>
                  <ElTableColumn prop="symbolId" label={t('合约')} minWidth={200}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return (
                          <div class={'flex align-center gap-row-8'}>
                            <img width={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(row.symbolId)} alt="" />
                            <div class={'symbol-info'}>
                              <span class="symbol-name">
                                {exchangeStore.getContractSymbolName(row.symbolId)} {t('永续')}
                                <ArrowRight />
                              </span>
                              <div class="flex info-tags gap-4">
                                <Tag color={row.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>
                                  {row.direction === OrderDirection.Buy ? t('做多') : t('做空')}
                                </Tag>
                                <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                  {row.marginType === 1 ? t('逐仓') : t('全仓')}
                                  {row.leverage}X
                                </Tag>
                              </div>
                            </div>
                          </div>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="position" label={t('持仓数量')} minWidth={160}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return (
                          <ControlledAmount>
                            {`${row.positionAmt.formatNumberWithCommas()} ${exchangeStore.getFromSymbolNameBySymbolId(row.symbolId)}`}&nbsp;/&nbsp;
                            {`${getPositionValue(row)} ${exchangeStore.getToSymbolNameBySymbolId(row.symbolId)}`}
                          </ControlledAmount>
                        )
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="holdPrice" label={t('开仓均价')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return <ControlledAmount>{row.holdPrice.formatNumberWithCommas()}</ControlledAmount>
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="markPrice" label={t('标记价格')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        if (props.fromAssetAccount) {
                          return <ControlledAmount>{getMarkPrice(row)?.formatNumberWithCommas() || '--'}</ControlledAmount>
                        }
                        return <span>{getMarkPrice(row)?.formatNumberWithCommas() || '--'}</span>
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="margin" label={t('保证金') + '(USDT)'} minWidth={100}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return <ControlledAmount>{row.margin.formatNumberWithCommas()}</ControlledAmount>
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="liquidationPrice" label={t('强平价')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return <ControlledAmount>{row.liquidationPrice.formatNumberWithCommas()}</ControlledAmount>
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="unrlzPnl" label={t('未实现盈亏')} minWidth={100}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return <Pnl value={getPnl(row)} />
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="收益率" label={t('收益率')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return <Pnl value={getPnlRate(row)} isRate />
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="updateTime" label={t('操作')} width={languageStore.isZH ? 320 : 'auto'}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return (
                          <div class={'action-btns'} onClick={(e) => e.stopPropagation()}>
                            <span onClick={() => handleClosePosition(row)}>{t('平仓')}</span>
                            {row.marginType === 1 && <span onClick={() => handleUpdateMargin(row)}>{t('调整保证金')}</span>}
                            <span onClick={() => handleStopProfitStopLoss(row)}>{t('止盈止损')}</span>
                            <span onClick={() => handlePositionDetail(row)}>{t('仓位详情')}</span>
                          </div>
                        )
                      },
                    }}
                  </ElTableColumn>
                </>
              ),
              empty: () => <FullScreenMinHeight>{!contractAssetsAccountStore.initLoading && <EmptyData />}</FullScreenMinHeight>,
            }}
          </HocElTable>
        </div>
      )
    }
  },
})
